import { motion } from "framer-motion";
import { PenTool } from "lucide-react";
import { IMG } from "@/lib/images";

const SKETCHES = [
  { img: IMG.GREECE_SKETCH, pos: "50% 30%", label: "The notebook", note: "Coffee, salt air, and a half-argued page of ideas.", tall: true },
  { img: IMG.SKETCH_BOARD, pos: "50% 50%", label: "First OUTLAW drafts", note: "Two bags. Forty rejected versions behind them." },
  { img: IMG.SKETCH_TECHPACK_PAGE, pos: "50% 50%", label: "From sketch to spec", note: "The page that became the tech pack." },
  { img: IMG.SKETCH_BENCH, pos: "50% 40%", label: "The workshop bench", note: "Where crossed-out ideas earn a second chance.", tall: true },
];

export const GarageWall = () => {
  return (
    <section id="garage-wall" data-testid="garage-wall-section" className="relative py-28 md:py-36 bg-[#0A0A0C] border-y border-white/5 overflow-hidden">
      {/* Faint blueprint grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(244,122,32,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(244,122,32,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-mark" />
              <span className="overline">Rough Ideas · Real Pages</span>
            </div>
            <h2 className="h-section text-white text-4xl sm:text-5xl lg:text-6xl" data-testid="garage-wall-heading">
              The Garage <span className="text-[#F47A20]">Wall.</span>
            </h2>
            <div className="mt-8 space-y-5 text-white/75 text-base sm:text-lg leading-relaxed max-w-xl">
              <p>
                Every great product starts with rough ideas.
              </p>
              <p>
                These are the real sketches, crossed-out concepts and coffee-stained notebook pages that slowly became the OUTLAW V1.
              </p>
              <p className="text-white font-semibold">
                Nothing here was outsourced. <br />
                Every page came from the garage.
              </p>
            </div>
            <div className="mt-8 inline-flex items-center gap-3 px-4 py-3 glass rounded-sm">
              <PenTool size={16} className="text-[#F47A20]" strokeWidth={1.6} />
              <span className="serial text-[10px] text-white/70">
                PEN · PAPER · PROBLEMS FIRST
              </span>
            </div>
          </motion.div>

          {/* Sketch wall */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-3 md:gap-4">
            {SKETCHES.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24, rotate: i % 2 ? 0.6 : -0.6 }}
                whileInView={{ opacity: 1, y: 0, rotate: i % 2 ? 0.6 : -0.6 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className={`group relative overflow-hidden rounded-sm border border-white/10 bg-[#141417] ${s.tall ? "aspect-[3/4]" : "aspect-square"} ${i === 1 ? "mt-8" : i === 3 ? "-mt-8" : ""}`}
                data-testid={`garage-wall-tile-${i}`}
              >
                <img
                  src={s.img}
                  alt={s.label}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: s.pos }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/90 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 serial text-[9px] text-[#F47A20] bg-[#0A0A0C]/70 backdrop-blur px-2 py-1 rounded-sm">
                  PAGE 0{i + 1}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="h-display text-base text-white leading-tight">{s.label}</div>
                  <p className="text-[12px] text-white/60 leading-snug mt-1">{s.note}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GarageWall;
